import React from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const generateRandomDelay = () => `${Math.random() * 0.5}s`; // generateing a random delay for wiggle animation

const ColorList = ({
  colors,
  selectedIndex,
  handleColorButtonClick,
  handleAddColor,
  deleteMode,
  toggleDeleteMode,
  keycapSetting,
  setting,
  currentColor,
  hasSelection = false,
  isUniformSelection = false,
  uniformSelectedIndex = null, // number or null
  showUnselectButton = null,
  unselectKeycaps = null,
}) => {
  const { t } = useTranslation();

  const getOverlaySymbol = (index) => {
    if (deleteMode || !hasSelection) return null; // if the user is in deleteMode or has no keycaps selected, return null (no overlay)

    if (isUniformSelection) {
      // if all selected keycaps reference the same color entry (or all reference no color at all):
      if (uniformSelectedIndex === null) return '+'; // if the uniformSelectedIndex is null, all selected keycaps have no index. Return "+" for all color entries, as the selected keycaps can be added to any color group.
      return uniformSelectedIndex === index ? '-' : '+'; // if the index of the current color entry is the same as the uniformSelectedIndex, return a "-" (to show the user that he can remove all selected keycaps from this color group). Otherwise return "+", to show the user he can add the selected keycaps to the group.
    }

    return '+'; // in case the user has selected keycaps, but they all reference different color entries/groups, return a "+" to show the user that he can add the current keycap selection to any color entry/group
  }; // this function will return the overlay symbol ("+", "-" or none) for the color buttons/entries

  return (
    <div className="color-list-container">
      {colors.map((color, index) => {
        const overlaySymbol = getOverlaySymbol(index); // get the overlay symbol ("+", "-" or none) for the current color button

        return (
          <div className="color-selector" key={index}>
            <Button
              variant="none"
              className={`${
                deleteMode
                  ? 'delete-mode-button'
                  : selectedIndex === index
                    ? 'selected-index-button'
                    : 'default-color-button'
              }`}
              style={{
                backgroundColor: color,
                border: deleteMode
                  ? '2px dashed black'
                  : selectedIndex === index
                    ? '2px solid black'
                    : `1px solid #f7f7f7`,
                animation: deleteMode
                  ? `wiggle 0.3s ${generateRandomDelay()} infinite`
                  : 'none',
              }}
              onClick={() =>
                handleColorButtonClick(index, keycapSetting, setting)
              }
              title={
                overlaySymbol
                  ? overlaySymbol === '+'
                    ? t('color-list-component-add-keycaps-to-group')
                    : t('color-list-component-remove-keycaps-to-group')
                  : undefined
              }
              aria-label={
                overlaySymbol
                  ? overlaySymbol === '+'
                    ? t('color-list-component-add-keycaps-to-group')
                    : t('color-list-component-remove-keycaps-to-group')
                  : undefined
              }
            >
              {deleteMode ? (
                <div className="color-button-dash">-</div>
              ) : overlaySymbol ? (
                <div
                  className={
                    overlaySymbol === '+'
                      ? 'color-button-overlay-plus'
                      : 'color-button-overlay-minus'
                  }
                >
                  {overlaySymbol}
                </div>
              ) : null}
            </Button>
          </div>
        );
      })}

      {colors.length < 20 && !deleteMode && (
        <div className="add-color-button-container">
          <Button
            variant="outline-primary"
            onClick={() => handleAddColor(currentColor, setting, keycapSetting)}
            disabled={colors.length >= 20} // Disable button if 20 colors are present
          >
            +
          </Button>
        </div>
      )}

      {showUnselectButton ? (
        <div className="unselect-keycaps-button-container">
          <Button
            variant="none"
            onClick={unselectKeycaps}
            title={t('color-list-component-unselect-keycaps')}
            aria-label={t('color-list-component-unselect-keycaps')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
              <path
                fill="#fe4a49"
                d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0M3.965 13.096a6.5 6.5 0 0 0 9.131-9.131ZM1.5 8a6.47 6.47 0 0 0 1.404 4.035l9.131-9.131A6.499 6.499 0 0 0 1.5 8"
              />
            </svg>
          </Button>
        </div>
      ) : colors.length > 0 ? (
        <div className="delete-color-button-container">
          <Button
            variant="none"
            onClick={toggleDeleteMode}
            disabled={colors.length <= 0} // Disable button there are no more colors to delete
          >
            {deleteMode ? (
              <div>
                <svg fill="#fe4a49" viewBox="0 0 16 16">
                  <g
                    id="SVGRepo_iconCarrier"
                    transform="matrix(0.30544377,0,0,0.30544377,-0.55018817,-0.55242556)"
                  >
                    <path d="m 41.1483,34.9258 3.5625,3.5625 1.1485,-24.0938 h 2.3439 c 0.9609,0 1.7342,-0.7969 1.7342,-1.7578 0,-0.9609 -0.7733,-1.7812 -1.7342,-1.7812 H 37.9608 V 7.3398 c 0,-3.3515 -2.2734,-5.5312 -5.8828,-5.5312 h -8.7656 c -3.6094,0 -5.8594,2.1797 -5.8594,5.5312 v 3.0235 h -0.8203 l 4.0078,4.0312 h 21.4688 z m 8.1795,17.25 c 0.7032,0.7031 1.8754,0.7031 2.555,0 0.6797,-0.7266 0.7032,-1.8516 0,-2.5547 L 6.6717,4.3867 c -0.7031,-0.7031 -1.8749,-0.7265 -2.578,0 -0.6797,0.6797 -0.6797,1.8516 0,2.5313 z M 21.203,10.8555 V 7.5742 c 0,-1.3359 0.9609,-2.2734 2.3907,-2.2734 h 8.2265 c 1.4297,0 2.4141,0.9375 2.4141,2.2734 v 3.2813 z m -4.2187,43.3359 h 21.4921 c 2.5079,0 4.4297,-1.2656 5.2032,-3.2812 l 0.0234,-0.4454 -3.1172,-3.0937 -0.0234,0.9141 c -0.0703,1.3828 -1.0547,2.3906 -2.4609,2.3906 H 17.3124 c -1.336,0 -2.3438,-1.0313 -2.4141,-2.3906 L 13.539,20.3242 9.6952,16.4805 11.2186,48.6836 c 0.1641,3.375 2.3672,5.5078 5.7657,5.5078 z m 12.164,-31.3125 v -2.625 c 0,-0.8437 -0.6562,-1.5234 -1.4297,-1.5234 -0.3984,0 -0.7968,0.2109 -1.0078,0.539 v 1.1954 z m 7.3125,7.3594 0.2813,-9.961 c 0.0234,-0.8906 -0.5625,-1.5468 -1.3594,-1.5468 -0.7266,0 -1.3359,0.6797 -1.3594,1.5234 l -0.2109,7.3359 z M 20.7812,46.5508 c 0.7968,0 1.3827,-0.6797 1.3593,-1.5235 l -0.4922,-16.5937 -2.789,-2.8125 0.539,19.4062 c 0.0235,0.8907 0.6094,1.5235 1.3829,1.5235 z m 6.9374,0 c 0.7735,0 1.4297,-0.6797 1.4297,-1.5235 v -9.1406 l -2.8359,-2.7891 v 11.9297 c 0,0.8438 0.6562,1.5235 1.4062,1.5235 z m 6.961,0 c 0.7734,0 1.3359,-0.6328 1.3594,-1.5235 l 0.0703,-2.1562 -2.6485,-2.6719 -0.1406,4.8281 c -0.0234,0.8438 0.5625,1.5235 1.3594,1.5235 z" />
                  </g>
                </svg>
              </div>
            ) : (
              <svg fill="#000000" viewBox="0 0 16 16">
                <g
                  id="SVGRepo_iconCarrier"
                  transform="matrix(0.30544377,0,0,0.30544377,-0.55242556,-0.55242556)"
                >
                  <path d="m 44.5235,48.6602 1.6172,-34.2657 h 2.3437 c 0.961,0 1.7343,-0.7969 1.7343,-1.7578 0,-0.9609 -0.7733,-1.7812 -1.7343,-1.7812 H 38.2422 V 7.3398 c 0,-3.3515 -2.2734,-5.5312 -5.8827,-5.5312 h -8.7657 c -3.6094,0 -5.8594,2.1797 -5.8594,5.5312 v 3.5157 H 7.5391 c -0.9375,0 -1.7578,0.8203 -1.7578,1.7812 0,0.9609 0.8203,1.7578 1.7578,1.7578 H 9.8829 L 11.5,48.6836 c 0.1641,3.375 2.3907,5.5078 5.7657,5.5078 h 21.4922 c 3.3516,0 5.6016,-2.1563 5.7656,-5.5312 z M 21.4844,7.5742 c 0,-1.3359 0.9844,-2.2734 2.3907,-2.2734 h 8.2265 c 1.4297,0 2.4141,0.9375 2.4141,2.2734 v 3.2813 H 21.4844 Z m -3.8671,43.1016 c -1.3594,0 -2.3673,-1.0313 -2.4376,-2.3906 L 13.5391,14.3945 h 28.8516 l -1.5469,33.8907 c -0.0703,1.3828 -1.0547,2.3906 -2.4375,2.3906 z m 17.3437,-4.125 c 0.7734,0 1.3594,-0.6328 1.3828,-1.5235 l 0.7031,-24.75 c 0.0235,-0.8906 -0.5859,-1.5468 -1.3828,-1.5468 -0.7265,0 -1.3359,0.6797 -1.3593,1.5234 l -0.7032,24.7734 c -0.0234,0.8438 0.5625,1.5235 1.3594,1.5235 z m -13.8984,0 c 0.7969,0 1.3828,-0.6797 1.3593,-1.5235 L 21.7188,20.2539 c -0.0234,-0.8437 -0.6562,-1.5234 -1.3828,-1.5234 -0.7969,0 -1.3828,0.6562 -1.3594,1.5468 l 0.7266,24.75 c 0.0234,0.8907 0.5859,1.5235 1.3594,1.5235 z m 8.3672,-1.5235 V 20.2539 c 0,-0.8437 -0.6329,-1.5234 -1.4063,-1.5234 -0.7735,0 -1.4297,0.6797 -1.4297,1.5234 v 24.7734 c 0,0.8438 0.6562,1.5235 1.4297,1.5235 0.75,0 1.4063,-0.6797 1.4063,-1.5235 z" />
                </g>
              </svg>
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default ColorList;
